import { useMemo, useState } from "react";
import { Eye, EyeOff, Pencil, Plus, Trash2, Image as ImageIcon, ExternalLink } from "lucide-react";
import { toast } from "sonner";
import { CHRIST_PRODUCTS } from "../data/christProducts";
import { OFFICINA_CATEGORIES } from "../data/officinaCategories";
import { fileUrl } from "../lib/products";
import { editProduct, createProduct, deleteProduct, reorderAttachments, formatApiError } from "../lib/adminApi";
import { Dropzone, AttachmentRow, ConfirmDialog } from "./AdminProductParts";

const API = process.env.REACT_APP_BACKEND_URL;

const STATIC_CATALOG = [
    ...CHRIST_PRODUCTS.map((p) => ({ key: p.key, group: "christ", name: `Christ ${p.name}`, sub: p.type, img: p.img, short: p.short })),
    ...OFFICINA_CATEGORIES.map((c) => ({ key: c.key, group: "officina", name: c.title, sub: c.brand || "Officina", img: c.img, short: c.desc })),
];

const SECTION = { christ: "schede-christ", officina: "autoriparatore" };
export const siteLink = (p) => `/?scheda=${p.key}#${SECTION[p.group]}`;

const input = "w-full rounded-xl border border-slate-200 bg-white px-4 py-3 text-sm text-[#0B1F3A] outline-none focus:border-[#0B1F3A]";
const lbl = "block text-xs font-bold uppercase tracking-wider text-[#4F5D70]";
const pill = "inline-flex items-center gap-1.5 rounded-full px-4 py-2 text-xs font-bold transition-colors disabled:opacity-50";

// Unisce catalogo statico e dati dal pannello (foto principale, descrizione breve, nascosto, prodotti aggiunti).
const mergeCatalog = (group, products) => {
    const fromStatic = STATIC_CATALOG.filter((p) => p.group === group).map((p) => {
        const d = products[p.key] || {};
        return { ...p, hidden: !!d.hidden, short: d.short || p.short, shortOverride: d.short || "", img: d.img ? `${API}${d.img}` : p.img, mainImageId: d.img ? d.img.split("/").pop() : "", attachments: d.attachments || [], custom: false };
    });
    const custom = Object.values(products)
        .filter((d) => d.custom && d.group === group)
        .sort((a, b) => (a.created_at || "").localeCompare(b.created_at || ""))
        .map((d) => ({ key: d.key, group, name: d.name, sub: d.sub, description: d.description, hidden: !!d.hidden, short: d.short, shortOverride: d.short, img: d.img ? `${API}${d.img}` : "", mainImageId: d.img ? d.img.split("/").pop() : "", attachments: d.attachments || [], custom: true }));
    return [...fromStatic, ...custom];
};

const EditForm = ({ p, onSaved, onClose }) => {
    const [short, setShort] = useState(p.shortOverride);
    const [name, setName] = useState(p.name);
    const [sub, setSub] = useState(p.sub || "");
    const [description, setDescription] = useState(p.description || "");
    const [busy, setBusy] = useState(false);
    const save = async (e) => {
        e.preventDefault();
        setBusy(true);
        try {
            await editProduct(p, { short, ...(p.custom ? { name, sub, description } : {}) });
            toast.success("Prodotto aggiornato");
            onSaved();
            onClose();
        } catch (err) {
            toast.error(formatApiError(err));
        } finally {
            setBusy(false);
        }
    };
    return (
        <form onSubmit={save} className="mt-4 grid gap-3 rounded-2xl bg-slate-50 p-4" data-testid={`admin-product-edit-${p.key}`}>
            {p.custom && (
                <>
                    <label className={lbl}>Nome prodotto<input value={name} onChange={(e) => setName(e.target.value)} className={`mt-1.5 ${input}`} data-testid={`admin-product-name-${p.key}`} /></label>
                    <label className={lbl}>Sottotitolo (tipo / marchio)<input value={sub} onChange={(e) => setSub(e.target.value)} className={`mt-1.5 ${input}`} data-testid={`admin-product-sub-${p.key}`} /></label>
                </>
            )}
            <label className={lbl}>
                Descrizione breve (sulla card del sito){!p.custom && " — vuota = testo originale"}
                <textarea value={short} onChange={(e) => setShort(e.target.value)} rows={3} maxLength={400} placeholder={p.custom ? "" : STATIC_CATALOG.find((s) => s.key === p.key)?.short} className={`mt-1.5 ${input}`} data-testid={`admin-product-short-${p.key}`} />
            </label>
            {p.custom && (
                <label className={lbl}>Descrizione completa (nella scheda; a capo = nuovo paragrafo)<textarea value={description} onChange={(e) => setDescription(e.target.value)} rows={5} maxLength={4000} className={`mt-1.5 ${input}`} data-testid={`admin-product-description-${p.key}`} /></label>
            )}
            <div className="flex flex-wrap gap-2">
                <button type="submit" disabled={busy} className={`${pill} bg-[#E30613] text-white hover:bg-[#C20410]`} data-testid={`admin-product-save-${p.key}`}>{busy ? "Salvataggio…" : "Salva"}</button>
                <button type="button" onClick={onClose} className={`${pill} border border-slate-200 bg-white text-[#0B1F3A]`}>Annulla</button>
            </div>
        </form>
    );
};

const ProductPanel = ({ p, onChanged }) => {
    const [edit, setEdit] = useState(false);
    const [confirmDelete, setConfirmDelete] = useState(false);
    const [busy, setBusy] = useState(false);
    const images = p.attachments.filter((a) => a.kind === "image");

    const apply = async (fields, msg) => {
        try {
            await editProduct(p, fields);
            toast.success(msg);
            onChanged();
        } catch (err) {
            toast.error(formatApiError(err));
        }
    };
    const move = async (index, dir) => {
        const ids = p.attachments.map((a) => a.id);
        const j = index + dir;
        [ids[index], ids[j]] = [ids[j], ids[index]];
        try {
            await reorderAttachments(p.key, ids);
            onChanged();
        } catch (err) {
            toast.error(formatApiError(err));
        }
    };
    const removeProduct = async () => {
        setBusy(true);
        try {
            await deleteProduct(p.key);
            toast.success("Prodotto eliminato");
            onChanged();
        } catch (err) {
            toast.error(formatApiError(err));
        } finally {
            setBusy(false);
        }
    };

    return (
        <article className={`rounded-3xl border bg-white p-4 shadow-[0_10px_30px_-12px_rgba(11,31,58,0.12)] sm:p-6 ${p.hidden ? "border-dashed border-slate-300 opacity-80" : "border-slate-200"}`} data-testid={`admin-product-${p.key}`}>
            <div className="flex items-start gap-4">
                {p.img ? (
                    <img src={p.img} alt="" className="h-20 w-28 shrink-0 rounded-2xl bg-slate-100 object-cover" data-testid={`admin-product-img-${p.key}`} />
                ) : (
                    <span className="flex h-20 w-28 shrink-0 items-center justify-center rounded-2xl bg-slate-100 text-[#94A3B8]"><ImageIcon className="h-6 w-6" /></span>
                )}
                <div className="min-w-0 flex-1">
                    <h2 className="font-display break-words text-lg font-extrabold leading-tight text-[#0B1F3A]">{p.name}</h2>
                    <p className="mt-0.5 text-xs uppercase tracking-wider text-[#4F5D70]">{p.sub}</p>
                    <div className="mt-2 flex flex-wrap gap-1.5">
                        <span className="rounded-full bg-slate-100 px-2.5 py-0.5 text-[10px] font-bold uppercase tracking-wider text-[#0B1F3A]" data-testid={`admin-count-${p.key}`}>{p.attachments.length} file</span>
                        {p.hidden && <span className="rounded-full bg-amber-100 px-2.5 py-0.5 text-[10px] font-bold uppercase tracking-wider text-amber-800" data-testid={`admin-product-hidden-badge-${p.key}`}>Nascosto sul sito</span>}
                        {p.custom && <span className="rounded-full bg-[#0B1F3A] px-2.5 py-0.5 text-[10px] font-bold uppercase tracking-wider text-white">Aggiunto dal pannello</span>}
                        {!p.custom && p.shortOverride && <span className="rounded-full bg-emerald-50 px-2.5 py-0.5 text-[10px] font-bold uppercase tracking-wider text-emerald-700">Descrizione modificata</span>}
                    </div>
                </div>
            </div>
            <p className="mt-3 text-sm leading-relaxed text-[#4F5D70]" data-testid={`admin-product-short-text-${p.key}`}>{p.short}</p>

            <div className="mt-4 flex flex-wrap gap-2">
                <a href={siteLink(p)} target="_blank" rel="noopener noreferrer" className={`${pill} border border-slate-200 bg-white text-[#0B1F3A] hover:bg-slate-50`} data-testid={`admin-product-site-${p.key}`}><ExternalLink className="h-3.5 w-3.5" /> Vedi sul sito</a>
                <button type="button" onClick={() => setEdit((v) => !v)} className={`${pill} border border-slate-200 bg-white text-[#0B1F3A] hover:bg-slate-50`} data-testid={`admin-product-edit-btn-${p.key}`}><Pencil className="h-3.5 w-3.5" /> Modifica testi</button>
                <button type="button" onClick={() => apply({ hidden: !p.hidden }, p.hidden ? "Prodotto di nuovo visibile sul sito" : "Prodotto nascosto dal sito")} className={`${pill} ${p.hidden ? "bg-emerald-600 text-white hover:bg-emerald-700" : "border border-slate-200 bg-white text-[#0B1F3A] hover:bg-slate-50"}`} data-testid={`admin-product-toggle-hidden-${p.key}`}>
                    {p.hidden ? <><Eye className="h-3.5 w-3.5" /> Mostra sul sito</> : <><EyeOff className="h-3.5 w-3.5" /> Nascondi dal sito</>}
                </button>
                {p.mainImageId && <button type="button" onClick={() => apply({ main_image_id: "" }, p.custom ? "Foto principale rimossa" : "Ripristinata la foto originale")} className={`${pill} border border-slate-200 bg-white text-[#0B1F3A] hover:bg-slate-50`} data-testid={`admin-product-reset-img-${p.key}`}><ImageIcon className="h-3.5 w-3.5" /> {p.custom ? "Togli foto principale" : "Foto originale"}</button>}
                {p.custom && <button type="button" onClick={() => setConfirmDelete(true)} className={`${pill} ml-auto border border-red-200 bg-white text-[#E30613] hover:bg-red-50`} data-testid={`admin-product-delete-${p.key}`}><Trash2 className="h-3.5 w-3.5" /> Elimina prodotto</button>}
            </div>
            {images.length > 0 && !p.mainImageId && <p className="mt-2 text-xs text-[#94A3B8]">Suggerimento: usa la stella su un'immagine caricata per impostarla come foto principale del prodotto.</p>}

            {edit && <EditForm p={p} onSaved={onChanged} onClose={() => setEdit(false)} />}

            {p.attachments.length > 0 && (
                <ul className="mt-4 space-y-2" data-testid={`admin-attachments-${p.key}`}>
                    {p.attachments.map((a, i) => (
                        <AttachmentRow key={a.id} product={p} a={a} index={i} total={p.attachments.length} isMain={a.id === p.mainImageId} onChanged={onChanged} onMove={(dir) => move(i, dir)} onSetMain={(id) => apply({ main_image_id: id }, "Foto principale aggiornata")} />
                    ))}
                </ul>
            )}
            <Dropzone product={p} onDone={onChanged} />
            <ConfirmDialog open={confirmDelete} onOpenChange={setConfirmDelete} title={`Eliminare il prodotto "${p.name}"?`} description="Verranno rimossi dal sito anche tutti i file collegati. L'operazione non può essere annullata." onConfirm={removeProduct} busy={busy} testId={`admin-product-delete-dialog-${p.key}`} />
        </article>
    );
};

const NewProductForm = ({ group, onCreated }) => {
    const [open, setOpen] = useState(false);
    const [f, setF] = useState({ name: "", sub: "", short: "", description: "" });
    const [busy, setBusy] = useState(false);
    const set = (k) => (e) => setF((v) => ({ ...v, [k]: e.target.value }));
    const submit = async (e) => {
        e.preventDefault();
        if (f.name.trim().length < 2) return toast.error("Inserisci il nome del prodotto");
        setBusy(true);
        try {
            await createProduct({ group, ...f });
            toast.success(`"${f.name}" creato: ora carica la foto e i depliant nel suo riquadro`);
            setF({ name: "", sub: "", short: "", description: "" });
            setOpen(false);
            onCreated();
        } catch (err) {
            toast.error(formatApiError(err));
        } finally {
            setBusy(false);
        }
    };
    if (!open) {
        return (
            <button type="button" onClick={() => setOpen(true)} className={`${pill} bg-[#0B1F3A] text-white hover:bg-[#132b4f]`} data-testid={`admin-new-product-${group}`}><Plus className="h-4 w-4" /> Nuovo prodotto</button>
        );
    }
    return (
        <form onSubmit={submit} className="grid gap-3 rounded-3xl border border-slate-200 bg-white p-5 sm:p-6" data-testid={`admin-new-product-form-${group}`}>
            <h2 className="font-display text-lg font-extrabold text-[#0B1F3A]">Nuovo prodotto — {group === "christ" ? "Autolavaggio Christ" : "Autoriparatore"}</h2>
            <label className={lbl}>Nome *<input value={f.name} onChange={set("name")} maxLength={80} className={`mt-1.5 ${input}`} data-testid="admin-new-product-name" /></label>
            <label className={lbl}>Sottotitolo (tipo / marchio)<input value={f.sub} onChange={set("sub")} maxLength={80} placeholder={group === "christ" ? "es. Portale di lavaggio" : "es. Ravaglioli"} className={`mt-1.5 ${input}`} data-testid="admin-new-product-sub" /></label>
            <label className={lbl}>Descrizione breve (card)<textarea value={f.short} onChange={set("short")} rows={2} maxLength={400} className={`mt-1.5 ${input}`} data-testid="admin-new-product-short" /></label>
            <label className={lbl}>Descrizione completa (scheda)<textarea value={f.description} onChange={set("description")} rows={4} maxLength={4000} className={`mt-1.5 ${input}`} data-testid="admin-new-product-description" /></label>
            <p className="text-xs text-[#94A3B8]">La foto principale e i depliant si caricano dopo, nel riquadro del nuovo prodotto (stella = foto principale).</p>
            <div className="flex flex-wrap gap-2">
                <button type="submit" disabled={busy} className={`${pill} bg-[#E30613] text-white hover:bg-[#C20410]`} data-testid="admin-new-product-save">{busy ? "Creazione…" : "Crea prodotto"}</button>
                <button type="button" onClick={() => setOpen(false)} className={`${pill} border border-slate-200 bg-white text-[#0B1F3A]`}>Annulla</button>
            </div>
        </form>
    );
};

export const AdminProducts = ({ group, products, onChanged }) => {
    const items = useMemo(() => mergeCatalog(group, products), [group, products]);
    return (
        <div className="mt-6" data-testid={`admin-products-${group}`}>
            <NewProductForm group={group} onCreated={onChanged} />
            <div className="mt-5 grid gap-5 xl:grid-cols-2">
                {items.map((p) => <ProductPanel key={p.key} p={p} onChanged={onChanged} />)}
            </div>
            <p className="mt-10 flex items-center gap-2 text-xs text-[#94A3B8]">
                <ImageIcon className="h-3.5 w-3.5" /> Formati accettati: PDF, JPG, PNG, WEBP, HEIC · max 40 MB per file · le immagini vengono ridotte a 2000 px e ripulite dai metadati.
            </p>
        </div>
    );
};
